import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Field, Form, reduxForm } from 'redux-form/immutable';
import fieldNames from './fieldNames';
import persInfoFields from '../../../PersonalInfoGP3/fieldsConfig';
import PersonalInfo from '../../../../containers/PersonalInfoGP3Container';
import renderSelect from '../../../../../../components/Select/reduxForm';
import renderRadio from '../../../../../../components/RadioGroup/reduxForm';
import { getRequiredFn } from '../../../../../../utils/reduxForm/validations';
import config from 'config';
import BiggestUserInspection from '../../../../../../components/BiggestUserInspection';

const required = getRequiredFn();

const PercentDepositsForm = ({
  formName,
  formProps: { handleSubmit },
  onSubmit,
  autofill,
  taxAuthority,
  periods,
  banks,
  year,
}) => {
  const [period, setPeriod] = useState(year);

  const taxAuthorityValue = useMemo(() => {
    return [
      {
        label: `${taxAuthority.code} — ${taxAuthority.name}`,
        value: taxAuthority.code,
      },
    ];
  }, [taxAuthority]);

  const banksValue = useMemo(() => {
    if (!period) {
      return [];
    }

    return banks[period].map((bank) => {
      if (bank?.reorganized) {
        return {
          label: bank.reorganized.name,
          value: bank.reorganized.inn,
        };
      }
      return {
        label: bank.name,
        value: bank.inn,
      };
    });
  }, [period]);

  const onPeriodChange = useCallback(({ value }) => {
    setPeriod(value);
    autofill(fieldNames.bank, '');
  }, []);

  return (
    <Form onSubmit={handleSubmit(onSubmit)} className="bg-white p-4 border mb-4">
      <PersonalInfo
        autofill={autofill}
        fields={[
          persInfoFields.inn,
          persInfoFields.lastName,
          persInfoFields.firstName,
          persInfoFields.middleName,
        ]}
        parentFormName={formName}
      />
      <BiggestUserInspection>
        <Field
          component={renderSelect}
          name={fieldNames.taxAuthority}
          label={'Налоговый орган'}
          placeholder={'Введите код или наименование налогового органа'}
          isRequired={true}
          options={taxAuthorityValue}
          isFull={true}
          isDisabled={true}
          validate={required}
        />
      </BiggestUserInspection>
      <Field
        component={renderSelect}
        name={fieldNames.yearf}
        label={'Период'}
        placeholder={'Выберите'}
        isRequired={true}
        options={periods}
        onChange={onPeriodChange}
        validate={required}
        isDisabled={config.isInspectorArm || year}
      />
      <Field
        component={renderSelect}
        name={fieldNames.bank}
        label={'Банк'}
        placeholder={'Выберите'}
        isRequired={true}
        options={banksValue}
        isDisabled={!period || config.isInspectorArm || year}
        validate={required}
      />
      <Field
        component={renderRadio}
        {...fieldNames.reasonNameNum}
        validate={required}
        isInline={true}
        isDisabled={config.isInspectorArm}
      />
    </Form>
  );
};

PercentDepositsForm.propTypes = {
  formName: PropTypes.any,
  formProps: PropTypes.object,
  onSubmit: PropTypes.func,
  autofill: PropTypes.func,
  taxAuthority: PropTypes.object,
  periods: PropTypes.array,
  banks: PropTypes.object,
  bankInn: PropTypes.string,
  year: PropTypes.string,
};

const mapStateToProps = (state, ownProps) => {
  const { formName } = ownProps;

  const getBankObj = () => {
    const bankInfoByYear = ownProps.year && ownProps.banks[ownProps.year];
    const bankPreSet = bankInfoByYear?.find((bank) => bank.inn === ownProps.bankInn);
    const reorganizedBank = bankInfoByYear?.find(
      (bank) => bank?.reorganized?.inn === ownProps.bankInn,
    );

    if (reorganizedBank) {
      return {
        label: reorganizedBank?.reorganized.name,
        value: reorganizedBank?.reorganized.inn,
      };
    }
    return { label: bankPreSet?.name, value: bankPreSet?.inn };
  };

  return {
    ...ownProps,
    form: formName,
    initialValues: {
      taxAuthority: ownProps.taxAuthority.code,
      yearf: ownProps.year && { label: ownProps.year, value: ownProps.year },
      bank: getBankObj(),
    },
  };
};

export default compose(
  connect(mapStateToProps),
  reduxForm({
    propNamespace: 'formProps',
    onSubmitFail: (values, dispatch, submitError, { resetTransition }) => {
      setTimeout(() => {
        const errorElement = document.querySelector('.form_field__error.red, .form_error.red');
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 200);

      resetTransition();
    },
    destroyOnUnmount: false,
  }),
)(PercentDepositsForm);
